import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getAllProductPurchases, ProductPurchase } from '../../services/productPurchaseService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ProductsPage: React.FC = () => {
  const [purchases, setPurchases] = useState<ProductPurchase[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<ProductPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPurchases();
  }, []);

  useEffect(() => {
    let result = [...purchases];

    if (paymentFilter !== 'all') {
      result = result.filter(purchase => purchase.paymentStatus === paymentFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(purchase => {
        return (
          purchase.customerName.toLowerCase().includes(term) ||
          purchase.customerEmail.toLowerCase().includes(term) ||
          purchase.confirmationCode.toLowerCase().includes(term) ||
          purchase.productName?.toLowerCase().includes(term) ||
          purchase.eventTitle?.toLowerCase().includes(term)
        );
      });
    }

    setFilteredPurchases(result);
  }, [purchases, paymentFilter, searchTerm]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const response = await getAllProductPurchases();
      if (response.success) {
        setPurchases(response.data);
      }
    } catch (error) {
      setError('Erro ao carregar pedidos de produtos');
      console.error('Erro ao carregar compras:', error);
    } finally {
      setLoading(false);
    }
  };



  const getPaymentStatusChip = (paymentStatus: string, status?: string) => {
    // Só considera como pago se ambos os status estiverem confirmados
    if (paymentStatus === 'paid' && status === 'confirmed') {
      return <Chip label="Pago" color="success" size="small" />;
    } else if (paymentStatus === 'paid' && status === 'pending') {
      return <Chip label="Processando" color="warning" size="small" />;
    } else if (paymentStatus === 'pending') {
      return <Chip label="Pendente" color="warning" size="small" />;
    } else if (paymentStatus === 'failed') {
      return <Chip label="Falhou" color="error" size="small" />;
    } else if (paymentStatus === 'refunded') {
      return <Chip label="Reembolsado" color="info" size="small" />;
    } else {
      return <Chip label={paymentStatus} size="small" />;
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  // Calcular estatísticas
  const stats = {
    totalPurchases: purchases.length,
    totalRevenue: purchases
      .filter(p => p.paymentStatus === 'paid' && p.status === 'confirmed')
      .reduce((sum, p) => sum + p.totalAmount, 0),
    pendingPayments: purchases.filter(p => p.paymentStatus === 'pending').length,
    paidPurchases: purchases.filter(p => p.paymentStatus === 'paid' && p.status === 'confirmed').length
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando pedidos...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          🛒 Pedidos de Produtos
        </Typography>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReceiptIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.totalPurchases}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Pedidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">R$ {stats.totalRevenue.toFixed(2)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Receita Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.paidPurchases}</Typography>
              <Typography variant="body2" color="text.secondary">
                Pedidos Pagos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.pendingPayments}</Typography>
              <Typography variant="body2" color="text.secondary">
                Pagamentos Pendentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Pedidos */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lista de Pedidos
          </Typography>

          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status do Pagamento</InputLabel>
                <Select
                  value={paymentFilter}
                  label="Status do Pagamento"
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="paid">Pago</MenuItem>
                  <MenuItem value="failed">Falhou</MenuItem>
                  <MenuItem value="refunded">Reembolsado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <MuiTextField
                fullWidth
                size="small"
                label="Buscar por nome, email ou código"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
          </Grid>

          {filteredPurchases.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum pedido encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {purchases.length === 0 ? 'Ainda não há pedidos registrados' : 'Nenhum pedido corresponde aos filtros aplicados'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Quantidade</TableCell>
                    <TableCell>Valor Total</TableCell>
                    <TableCell>Status do Pagamento</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow key={purchase._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {purchase.customerName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {purchase.customerEmail}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {purchase.confirmationCode}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {purchase.productName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          R$ {purchase.productPrice?.toFixed(2).replace('.', ',')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {purchase.eventTitle}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {purchase.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          R$ {purchase.totalAmount.toFixed(2).replace('.', ',')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusChip(purchase.paymentStatus, purchase.status)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(purchase.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ProductsPage; 